// client/src/hooks/usePlan.ts

import { useState, useEffect } from "react";
import { PLANS, PlanType } from "@shared/plans";
import { getCurrentOwner } from "@/lib/auth";

export function usePlan() {
  const [planType, setPlanType] = useState<PlanType>("basic");
  const [planLimits, setPlanLimits] = useState(PLANS.basic);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Obtener el owner del localStorage
    const owner = getCurrentOwner();

    if (owner && owner.planType) {
      const type = owner.planType as PlanType;
      setPlanType(type);
      setPlanLimits(PLANS[type] || PLANS.basic);
    }

    setLoading(false);
  }, []);

  // =====================================================
  // {FUNCIONES DE VERIFICACIÓN DE CARACTERÍSTICAS}
  // =====================================================
  const can = {
    // ========== LÍMITES CUANTITATIVOS ==========
    canCreateShop: (currentShops: number) => currentShops < planLimits.maxShops,
    canCreateProduct: (currentProducts: number) =>
      currentProducts < planLimits.maxProducts,
    canCreateUser: (currentUsers: number) =>
      currentUsers < planLimits.maxUsersPerShop,
    hasRole: (role: string) => planLimits.userRoles.includes(role),

    // ========== CARACTERÍSTICAS DEL POS ==========
    useShiftManagement: () => planLimits.posFeatures.shiftManagement,
    usePettyCash: () => planLimits.posFeatures.pettyCash,
    useBlindBox: () => planLimits.posFeatures.blindBox,

    // ========== MÓDULOS DE INVENTARIO ==========
    // {Inventario básico: precio, stock, umbral - disponible en todos los planes}
    // {Inventario avanzado: análisis, proveedores, lotes, fechas de expiración}
    useAdvancedInventory: () => planLimits.modules.inventory === "advanced",

    // ========== MÓDULO DE CLIENTES ==========
    useCustomers: () => planLimits.modules.customers,

    // ========== MÓDULO DE DEVOLUCIONES ==========
    useReturns: () => {
      // Por ahora, asumimos que las devoluciones están disponibles en Professional y Enterprise
      // Si en el futuro se agrega a shared/plans.ts, podemos usar planLimits.modules.returns
      return planType === "professional" || planType === "enterprise";
    },

    // ========== NIVELES DE REPORTES ==========
    // {Reportes básicos: ventas, productos, clientes, discrepancias, transferencias}
    useReports: () => planLimits.modules.reports !== "none",
    // {Reportes enterprise: todo lo básico + consolidados multi-tienda + heatmaps}
    useEnterpriseReports: () => planLimits.modules.reports === "enterprise",
    // {Acceso al OwnerDashboardKPI (solo enterprise)}
    useConsolidatedReports: () => planLimits.modules.reports === "enterprise",

    // ========== TELEGRAM ==========
    useTelegram: () => planLimits.modules.telegram,

    // ========== NIVELES DE SOPORTE ==========
    // {Soporte dedicado: WhatsApp/Telegram dedicado (professional)}
    hasDedicatedSupport: () =>
      planLimits.support.level === "dedicated" ||
      planLimits.support.level === "premium",
    // {Soporte premium: incluye onboarding personalizado y tiempo de respuesta (enterprise)}
    hasPremiumSupport: () => planLimits.support.level === "premium",
    hasOnboarding: () => planLimits.support.onboarding,
    getResponseTime: () => planLimits.support.responseTime,
  };

  // =====================================================
  // {FUNCIONES DE AYUDA PARA DEPURACIÓN Y UI}
  // =====================================================
  const getReportLevel = () => planLimits.modules.reports;
  const getSupportLevel = () => planLimits.support.level;
  const getInventoryLevel = () => planLimits.modules.inventory;
  const getPosFeatures = () => planLimits.posFeatures;

  return {
    planType,
    planLimits,
    loading,
    can,
    // Nombre amigable del plan
    planName:
      planType === "basic"
        ? "Básico"
        : planType === "professional"
          ? "Profesional"
          : "Enterprise",
    // Funciones de ayuda
    getReportLevel,
    getSupportLevel,
    getInventoryLevel,
    getPosFeatures,
  };
}
